import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const PointsContext = createContext();

export const usePoints = () => {
  const context = useContext(PointsContext);
  if (!context) {
    throw new Error('usePoints must be used within a PointsProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';
const DEFAULT_BALANCE = 30;
// Design 034: balance may go negative down to this hard floor (was 0).
// Keep in sync with MIN_SUBSCRIBER_BALANCE on the server (the authoritative floor).
const MIN_BALANCE = -10;

export const PointsProvider = ({ children }) => {
  const [balance, setBalance] = useState(DEFAULT_BALANCE);
  const [loading, setLoading] = useState(false);
  const pubIdRef = useRef(null);
  const tokenRef = useRef(null);

  // Called by VideoFeedWrapper on mount to fetch the real server balance
  const initBalance = useCallback(async (publicationId, idToken) => {
    pubIdRef.current = publicationId;
    tokenRef.current = idToken;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/subscriber/balance/${encodeURIComponent(publicationId)}`,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setBalance(data.balance ?? DEFAULT_BALANCE);
      } else if (res.status === 404) {
        // Not subscribed yet or row missing — use default
        setBalance(DEFAULT_BALANCE);
      }
    } catch {
      // Network error — keep default
    } finally {
      setLoading(false);
    }
  }, []);

  // Optimistic deduction + server call. videoId is required for idempotency.
  const deductPoints = useCallback(async (amount, videoId) => {
    // Optimistic update immediately (floor mirrors the server's MIN_SUBSCRIBER_BALANCE)
    setBalance(prev => Math.max(MIN_BALANCE, prev - amount));

    const pubId = pubIdRef.current;
    const token = tokenRef.current;
    if (!pubId || !token) return;

    try {
      const res = await fetch(
        `${API_URL}/api/subscriber/balance/${encodeURIComponent(pubId)}/deduct`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ video_id: videoId || '', points: amount }),
        }
      );
      if (res.ok) {
        const data = await res.json();
        // Sync to server's authoritative balance
        setBalance(data.balance_after);
      }
    } catch {
      // Server call failed — optimistic update stands, will reconcile on next init
    }
  }, []);

  const addPoints = useCallback((amount) => {
    setBalance(prev => prev + amount);
  }, []);

  const resetPoints = useCallback((newBalance = DEFAULT_BALANCE) => {
    setBalance(newBalance);
  }, []);

  const hasEnoughPoints = useCallback((amount) => {
    return balance >= amount;
  }, [balance]);

  const value = {
    balance,
    loading,
    initBalance,
    deductPoints,
    addPoints,
    resetPoints,
    hasEnoughPoints,
  };

  return (
    <PointsContext.Provider value={value}>
      {children}
    </PointsContext.Provider>
  );
};

export default PointsContext;

